import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Textarea,
  Input,
  VStack,
  Stack,
  Divider,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Badge,
  Flex,
} from "@chakra-ui/react";
import {
  ExamManualAttemptDetails,
  ExamAttemptedManualQuestionSet,
  AttemptedManualQuestion,
  ExamManualAttemptDetailsEval,
  ExamAttemptScoreStatus,
} from "../Common/types";
import axios from "axios";
import { RenderQuestionContext, RenderTextWithImages, QuestionContext } from "../../utils/questionRendering";

interface ExamAttemptManualEvaluatorProps {
  examManualAttemptDetails: ExamManualAttemptDetails;
  onSubmit: (payload: ExamManualAttemptDetailsEval) => void;
}

const ExamAttemptManualEvaluator: React.FC<ExamAttemptManualEvaluatorProps> = ({
  examManualAttemptDetails,
  onSubmit,
}) => {
  const [remarksByQuestionId, setRemarksByQuestionId] = useState<Record<number, string>>({});
  const [marksByQuestionId, setMarksByQuestionId] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<Record<number, boolean>>({});

  const hasValidContext = (ctx: any): ctx is QuestionContext =>
    Boolean(ctx) && typeof ctx === 'object' && 'type' in ctx;

  const handleInputChange = (qid: number, value: string) => {
    setMarksByQuestionId((prev) => ({ ...prev, [qid]: value }));
  };

  const handleRemarksChange = (qid: number, value: string) => {
    setRemarksByQuestionId((prev) => ({ ...prev, [qid]: value }));
  };

  useEffect(() => {
    if (examManualAttemptDetails) {
      const initialMarks: Record<number, string> = {};
      const initialRemarks: Record<number, string> = {};

      examManualAttemptDetails.attempted_manual_question_sets.forEach((qs) => {
        qs.attempted_manual_questions.forEach((q) => {
          if (q.awarded_marks !== null && q.awarded_marks !== undefined) {
            initialMarks[q.exam_attempted_question_id] = q.awarded_marks.toString();
          }
          if (q.tutor_remarks) {
            initialRemarks[q.exam_attempted_question_id] = q.tutor_remarks;
          }
        });
      });

      setMarksByQuestionId(initialMarks);
      setRemarksByQuestionId(initialRemarks);
    }
  }, [examManualAttemptDetails]);

  const validateAndSubmit = async () => {
    const newErrors: Record<number, boolean> = {};
    let hasError = false;

    examManualAttemptDetails.attempted_manual_question_sets.forEach((qs) => {
      qs.attempted_manual_questions.forEach((q) => {
        const marksStr = marksByQuestionId[q.exam_attempted_question_id];
        const marks = Number(marksStr);
        const remarks = remarksByQuestionId[q.exam_attempted_question_id];

        const isEmptyMarks = marksStr === undefined || marksStr === "";
        const isInvalidRemarks = remarks?.trim() === "";

        if (isEmptyMarks || isInvalidRemarks || isNaN(marks) || marks > q.maximum_marks) {
          newErrors[q.exam_attempted_question_id] = true;
          hasError = true;
        }
      });
    });

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    const payload: ExamManualAttemptDetailsEval = {
      exam_attempt_id: examManualAttemptDetails.exam_attempt_id,
      attempted_manual_question_sets: examManualAttemptDetails.attempted_manual_question_sets.map((qs) => ({
        exam_attempted_question_set_id: qs.exam_attempted_question_set_id,
        attempted_manual_questions: qs.attempted_manual_questions.map((q) => ({
          exam_attempted_question_id: q.exam_attempted_question_id,
          awarded_marks: Number(marksByQuestionId[q.exam_attempted_question_id]),
          tutor_remarks: remarksByQuestionId[q.exam_attempted_question_id],
        })),
      })),
    };

    onSubmit(payload);
  };

  return (
    <Box mx="auto" overflowY="auto">
      <Heading size="md" mb={4}>
        Manual Evaluation - {examManualAttemptDetails.student_name}
      </Heading>

      {examManualAttemptDetails.attempted_manual_question_sets.map((qs) => (
        <Box key={qs.exam_attempted_question_set_id} borderWidth="1px" borderRadius="lg" p={4} mb={6}>
          <Heading size="sm" mb={4}>
            Question Set: {qs.question_set_name}
          </Heading>

          <VStack align="stretch" spacing={4}>
            {[...qs.attempted_manual_questions]
              .sort((a, b) => a.sequence_no - b.sequence_no)
              .map((q) => (
                <Box key={q.exam_attempted_question_id} p={4} borderWidth="1px" borderRadius="md">
                  <Stack spacing={2}>
                    <Badge colorScheme="blue" fontSize="xs" width="fit-content">
                      Q{q.sequence_no}
                    </Badge>

                    {/* Split or full-width based on question_context --
                        question_image removed, question_text now uses
                        RenderTextWithImages to support embedded {{image:...}} tags */}
                    {hasValidContext(q?.question_context) ? (
                      <Flex gap={4} align="stretch">
                        <Box w="45%" p={3} border="1px solid" borderColor="blue.200"
                          borderRadius="md" bg="blue.50" overflowY="auto" maxH="300px">
                          <Text fontSize="xs" fontWeight="bold" color="blue.500" mb={2}>CONTEXT</Text>
                          <RenderQuestionContext context={q.question_context} />
                        </Box>
                        <Box w="55%">
                          <RenderTextWithImages
                            text={q.question_text ?? ""}
                            fontSize="sm"
                            fontWeight="semibold"
                          />
                        </Box>
                      </Flex>
                    ) : (
                      q.question_text && (
                        <RenderTextWithImages
                          text={q.question_text}
                          fontSize="sm"
                          fontWeight="semibold"
                        />
                      )
                    )}

                    <Text fontSize="sm" color="gray.600">
                      Question Type: {q.question_type}
                    </Text>

                    <Divider />

                    {q.text_response ? (
                      <Box bg="gray.50" p={2} borderRadius="md">
                        <Text fontWeight="medium" mb={1}>Student Response:</Text>
                        <Text fontSize="sm">{q.text_response}</Text>
                      </Box>
                    ) : (
                      <Text fontSize="sm" color="gray.500">No response submitted.</Text>
                    )}

                    <Text fontSize="sm" color="gray.600">
                      Maximum Marks: {q.maximum_marks}
                    </Text>

                    <Divider />

                    <FormControl isInvalid={errors[q.exam_attempted_question_id]}>
                      <FormLabel>Awarded Marks *</FormLabel>
                      <Input
                        type="number"
                        value={marksByQuestionId[q.exam_attempted_question_id] || ""}
                        onChange={(e) => handleInputChange(q.exam_attempted_question_id, e.target.value)}
                      />
                      <FormErrorMessage>Marks are required</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={errors[q.exam_attempted_question_id]}>
                      <FormLabel>Remarks *</FormLabel>
                      <Textarea
                        value={remarksByQuestionId[q.exam_attempted_question_id] || ""}
                        onChange={(e) => handleRemarksChange(q.exam_attempted_question_id, e.target.value)}
                      />
                      <FormErrorMessage>Remarks are required</FormErrorMessage>
                    </FormControl>
                  </Stack>
                </Box>
              ))}
          </VStack>
        </Box>
      ))}

      <Button mt={4} colorScheme="green" onClick={() => validateAndSubmit()}>
        Save Evaluation
      </Button>
    </Box>
  );
};

export default ExamAttemptManualEvaluator;